/**
 * Suppression List Service
 * Manages global email and SMS suppression for campaigns
 */

import { supabase } from "./supabase";
import type { UUID } from "../types/common.types";

export interface SuppressedContact {
  id: UUID;
  email: string;
  first_name: string | null;
  last_name: string | null;
  email_opt_in: boolean;
  email_opt_out_at: string | null;
  sms_opt_in: boolean;
  sms_opt_out_at: string | null;
  suppression_reason?: string;
}

export interface SuppressionStats {
  total_contacts: number;
  email_suppressed: number;
  sms_suppressed: number;
  both_suppressed: number;
}

/**
 * Get all suppressed contacts (email opt-out)
 */
export async function getSuppressedContacts(
  type: "email" | "sms" | "both" = "email",
): Promise<SuppressedContact[]> {
  let query = supabase
    .from("contacts")
    .select(
      "id, email, first_name, last_name, email_opt_in, email_opt_out_at, sms_opt_in, sms_opt_out_at",
    );

  if (type === "email") {
    query = query.or("email_opt_in.eq.false,email_opt_out_at.not.is.null");
  } else if (type === "sms") {
    query = query.or("sms_opt_in.eq.false,sms_opt_out_at.not.is.null");
  } else {
    query = query.or(
      "email_opt_in.eq.false,email_opt_out_at.not.is.null,sms_opt_in.eq.false,sms_opt_out_at.not.is.null",
    );
  }

  const { data, error } = await query.order("email_opt_out_at", {
    ascending: false,
    nullsFirst: false,
  });

  if (error) {
    console.error("Error fetching suppressed contacts:", error);
    throw error;
  }

  return data || [];
}

/**
 * Get suppression statistics
 */
export async function getSuppressionStats(): Promise<SuppressionStats> {
  // Get total count
  const { count: totalContacts } = await supabase
    .from("contacts")
    .select("*", { count: "exact", head: true });

  // Get email suppressed count
  const { count: emailSuppressed } = await supabase
    .from("contacts")
    .select("*", { count: "exact", head: true })
    .or("email_opt_in.eq.false,email_opt_out_at.not.is.null");

  // Get SMS suppressed count
  const { count: smsSuppressed } = await supabase
    .from("contacts")
    .select("*", { count: "exact", head: true })
    .or("sms_opt_in.eq.false,sms_opt_out_at.not.is.null");

  // Get both suppressed count
  const { count: bothSuppressed } = await supabase
    .from("contacts")
    .select("*", { count: "exact", head: true })
    .or("email_opt_in.eq.false,email_opt_out_at.not.is.null")
    .or("sms_opt_in.eq.false,sms_opt_out_at.not.is.null");

  return {
    total_contacts: totalContacts || 0,
    email_suppressed: emailSuppressed || 0,
    sms_suppressed: smsSuppressed || 0,
    both_suppressed: bothSuppressed || 0,
  };
}

/**
 * Suppress a contact (opt-out from email)
 */
export async function suppressContact(
  contactId: UUID,
  type: "email" | "sms" | "both" = "email",
  _reason?: string,
): Promise<void> {
  const updates: Record<string, unknown> = {
    updated_at: new Date().toISOString(),
  };

  if (type === "email" || type === "both") {
    updates.email_opt_in = false;
    updates.email_opt_out_at = new Date().toISOString();
  }

  if (type === "sms" || type === "both") {
    updates.sms_opt_in = false;
    updates.sms_opt_out_at = new Date().toISOString();
  }

  const { error } = await supabase
    .from("contacts")
    .update(updates)
    .eq("id", contactId);

  if (error) {
    console.error("Error suppressing contact:", error);
    throw error;
  }
}

/**
 * Unsuppress a contact (re-enable email)
 */
export async function unsuppressContact(
  contactId: UUID,
  type: "email" | "sms" | "both" = "email",
): Promise<void> {
  const updates: Record<string, unknown> = {
    updated_at: new Date().toISOString(),
  };

  if (type === "email" || type === "both") {
    updates.email_opt_in = true;
    updates.email_opt_out_at = null;
  }

  if (type === "sms" || type === "both") {
    updates.sms_opt_in = true;
    updates.sms_opt_out_at = null;
  }

  const { error } = await supabase
    .from("contacts")
    .update(updates)
    .eq("id", contactId);

  if (error) {
    console.error("Error unsuppressing contact:", error);
    throw error;
  }
}

/**
 * Bulk suppress contacts by email addresses
 */
export async function bulkSuppressByEmail(
  emails: string[],
  type: "email" | "sms" | "both" = "email",
): Promise<{ suppressed: number; notFound: string[] }> {
  const notFound: string[] = [];
  let suppressed = 0;

  for (const email of emails) {
    const normalizedEmail = email.toLowerCase().trim();

    // Find contact by email
    const { data: contact } = await supabase
      .from("contacts")
      .select("id")
      .eq("email", normalizedEmail)
      .single();

    if (contact) {
      await suppressContact(contact.id, type);
      suppressed++;
    } else {
      notFound.push(email);
    }
  }

  return { suppressed, notFound };
}

/**
 * Search suppressed contacts
 */
export async function searchSuppressedContacts(
  query: string,
  type: "email" | "sms" | "both" = "email",
): Promise<SuppressedContact[]> {
  // Smart-detection. Leading-wildcard ILIKE under RLS on 631k contacts
  // can drop into the has_org_access per-row plan shape and approach
  // statement-timeout. Pattern mirrors getContacts
  // (contacts.service.ts:80-132).
  //   @-in-term → exact email lookup
  //   otherwise → prefix-only first_name OR last_name
  let dbQuery = supabase
    .from("contacts")
    .select(
      "id, email, first_name, last_name, email_opt_in, email_opt_out_at, sms_opt_in, sms_opt_out_at",
    );
  const term = query.trim();
  if (term.includes("@")) {
    dbQuery = dbQuery.eq("email", term.toLowerCase());
  } else {
    const sanitized = term.replace(/[,()."*\\]/g, "");
    if (sanitized) {
      dbQuery = dbQuery.or(
        `first_name.ilike.${sanitized}%,last_name.ilike.${sanitized}%`,
      );
    }
  }

  if (type === "email") {
    dbQuery = dbQuery.or("email_opt_in.eq.false,email_opt_out_at.not.is.null");
  } else if (type === "sms") {
    dbQuery = dbQuery.or("sms_opt_in.eq.false,sms_opt_out_at.not.is.null");
  }

  const { data, error } = await dbQuery.limit(100);

  if (error) {
    console.error("Error searching suppressed contacts:", error);
    throw error;
  }

  return data || [];
}

/**
 * Check if an email is suppressed
 */
export async function isEmailSuppressed(email: string): Promise<boolean> {
  const normalizedEmail = email.toLowerCase().trim();

  const { data } = await supabase
    .from("contacts")
    .select("email_opt_in, email_opt_out_at")
    .eq("email", normalizedEmail)
    .single();

  if (!data) {
    return false;
  }

  return data.email_opt_in === false || data.email_opt_out_at !== null;
}

/**
 * Get contacts that are NOT suppressed (for campaign targeting)
 */
export async function getEligibleContactIds(
  contactIds: UUID[],
  type: "email" | "sms" = "email",
): Promise<UUID[]> {
  const optInField = type === "email" ? "email_opt_in" : "sms_opt_in";
  const optOutField = type === "email" ? "email_opt_out_at" : "sms_opt_out_at";

  const { data, error } = await supabase
    .from("contacts")
    .select("id")
    .in("id", contactIds)
    .eq(optInField, true)
    .is(optOutField, null);

  if (error) {
    console.error("Error filtering eligible contacts:", error);
    throw error;
  }

  return (data || []).map((c) => c.id);
}
